// MODEL INSPECTION + DECISION TRACE (Corrective Build §2 + §6). READ-ONLY.
//
// §2: "a read-only page showing every runner in every race" — raw + weighted factor scores, total
// model score, rank, win prob, fair price, reliability, eligibility, failed gates, final authority.
// §6: "every horse ... one complete decision trace. No horse should disappear without a recorded
// reason." Both are the same per-runner object from the backend (decisionTrace), which assembles the
// exact production outputs and recomputes NOTHING.
import React, { useCallback, useEffect, useState } from 'react';
import { getInspection, downloadInspectionCsv, InspectionView, RunnerTrace } from '../api/strategy';

const f2 = (n: number | null | undefined) => (n == null ? '—' : Number(n).toFixed(2));
const price = (n: number | null | undefined) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const pct = (n: number | null | undefined) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const today = () => new Date().toISOString().slice(0, 10);

const STATE_COLOUR: Record<string, string> = {
  LIVE: '#166534', PAPER: '#6b7280', CULLED: '#b45309',
  // §3/§4 — retained-and-reassessed states, amber not red: the runner is monitored, not discarded.
  PRICE_PENDING: '#b45309', PRICE_INTEGRITY_QUARANTINE: '#b91c1c',
  REJECTED: '#991b1b', PENDING: '#374151',
};

const StatePill: React.FC<{ s: string }> = ({ s }) => (
  <span style={{ fontSize: '0.72em', fontWeight: 700, color: STATE_COLOUR[s] || '#374151',
    border: `1px solid ${STATE_COLOUR[s] || '#374151'}`, borderRadius: 3, padding: '0 5px' }}>{s}</span>
);

// The per-factor breakdown — raw z-score, weight, and the weighted contribution that actually enters
// the composite. Factor 1 (peaking) and 2 (ability) are highlighted for the §5 fitness review.
const FactorTable: React.FC<{ r: RunnerTrace }> = ({ r }) => (
  <table className="sr-table" style={{ fontSize: '0.8em', width: '100%', marginTop: 6 }}>
    <thead><tr>
      <th>Factor</th><th className="sr-num">Weight</th><th className="sr-num" title="The adjusted z-scored factor score the model used">Raw score</th>
      <th className="sr-num" title="weight × score — what actually enters the composite">Weighted</th>
      <th className="sr-num">Coverage</th><th>Note</th>
    </tr></thead>
    <tbody>
      {r.factors.map((f) => (
        <tr key={f.id} style={{ background: f.id === '1' ? '#fffbeb' : f.id === '2' ? '#f0f9ff' : undefined }}>
          <td>{f.id}. {f.name}{f.id === '1' && ' (peaking)'}{f.id === '2' && ' (ability)'}</td>
          <td className="sr-num">{f.weight == null ? '—' : (f.weight * 100).toFixed(0) + '%'}</td>
          <td className="sr-num" style={{ color: (f.raw_score ?? 0) < 0 ? '#991b1b' : '#166534' }}>{f2(f.raw_score)}</td>
          <td className="sr-num">{f2(f.weighted_contribution)}</td>
          <td className="sr-num">{f2(f.coverage)}</td>
          <td style={{ fontSize: '0.9em', color: '#9ca3af' }}>{f.defaulted ? 'largely defaulted (cov < 0.5)' : ''}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const RunnerRow: React.FC<{ r: RunnerTrace }> = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr onClick={() => setOpen(!open)} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
        background: r.final_state === 'LIVE' ? '#f0fdf4' : undefined }}>
        <td className="sr-num">{r.model_rank ?? '—'}</td>
        <td style={{ fontWeight: r.model_rank === 1 ? 700 : 500 }}>{open ? '▾ ' : '▸ '}{r.runner}</td>
        <td className="sr-num">{pct(r.win_pct)}</td>
        <td className="sr-num">{price(r.fair_price)}</td>
        <td className="sr-num">{f2(r.total_model_score)}</td>
        <td className="sr-num" title="Factor 1 — peaking (the slope)"
          style={{ color: (r.peaking_score ?? 0) < 0 ? '#991b1b' : '#166534' }}>{f2(r.peaking_score)}</td>
        <td className="sr-num" title="Factor 2 — ability (the level)">{f2(r.ability_score)}</td>
        <td className="sr-num">{r.reliability == null ? '—' : `${r.reliability}%`}</td>
        <td>{r.engine_nominated || '—'}</td>
        <td><StatePill s={r.final_state} /></td>
      </tr>
      {open && (
        <tr>
          <td colSpan={10} style={{ background: '#fafafa', padding: '8px 12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: '0.78em', textTransform: 'uppercase', color: '#6b7280' }}>Model breakdown</div>
                <FactorTable r={r} />
                <div style={{ fontSize: '0.8em', color: '#6b7280', marginTop: 4 }}>
                  overall coverage {f2(r.coverage_overall)} · race state {r.race_state ?? '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.78em', textTransform: 'uppercase', color: '#6b7280' }}>Decision trace</div>
                {/* §6: authority, gates, and the reason — the complete trace, never blank. */}
                <div style={{ fontSize: '0.85em', marginTop: 6 }}>
                  <div><strong>Final authority:</strong> {r.final_authority.live
                    ? <span style={{ color: '#166534' }}>LIVE · {r.final_authority.policy}</span>
                    : <span style={{ color: '#6b7280' }}>no live authority — {r.final_authority.reason}</span>}</div>
                  {r.gates_passed.length > 0 && (
                    <div style={{ marginTop: 4 }}><strong style={{ color: '#166534' }}>Passed:</strong>{' '}
                      {r.gates_passed.map((g, i) => <span key={i}>{g.gate}{i < r.gates_passed.length - 1 ? ', ' : ''}</span>)}</div>
                  )}
                  {r.gates_failed.length > 0 && (
                    <div style={{ marginTop: 4 }}><strong style={{ color: '#991b1b' }}>Failed:</strong>{' '}
                      {r.gates_failed.map((g, i) => <div key={i} style={{ marginLeft: 12 }}>• {g.gate}{g.detail ? ` — ${g.detail}` : ''}</div>)}</div>
                  )}
                  {r.primary_rejection_reason && (
                    <div style={{ marginTop: 4 }}><strong>Primary reason:</strong> {r.primary_rejection_reason}
                      {r.reason_code && <span style={{ color: '#9ca3af' }}> [{r.reason_code}]</span>}</div>
                  )}
                  {r.gates_passed.length === 0 && r.gates_failed.length === 0 && !r.primary_rejection_reason && (
                    <div style={{ color: '#9ca3af', marginTop: 4 }}>No gate outcomes recorded for this runner.</div>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const ModelInspection: React.FC = () => {
  const [date, setDate] = useState(today());
  const [view, setView] = useState<InspectionView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try { setView(await getInspection(date)); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); setView(null); }
    finally { setBusy(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ marginBottom: 2 }}>Model Inspection</h1>
      <div style={{ color: '#6b7280', fontSize: '0.85em', marginBottom: 12 }}>
        Every runner, every race — the exact production model outputs with a complete decision trace.
        Read-only; nothing here is recomputed. Click a runner for its factor breakdown and trace.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <button onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
        <button onClick={() => downloadInspectionCsv(date).catch((e) => setErr(String(e)))}>Download CSV</button>
        {view && <span style={{ fontSize: '0.82em', color: '#6b7280' }}>{view.runners_traced} runners across {view.races.length} races</span>}
      </div>

      {err && <div style={{ color: '#991b1b', fontSize: '0.85em' }}>{err}</div>}
      {view && view.races.length === 0 && <p style={{ color: '#6b7280' }}>No scored runners recorded for {date}.</p>}

      {view && view.races.map((rc) => (
        <div key={rc.race_id} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {rc.track} R{rc.race} <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85em' }}>· {rc.runners.length} of {rc.field_size} scored</span>
            {!rc.all_traced && <span style={{ color: '#991b1b', fontSize: '0.8em' }}> · ⚠ a runner is missing its state</span>}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="sr-table" style={{ fontSize: '0.85em', width: '100%' }}>
              <thead><tr>
                <th className="sr-num">#</th><th>Runner</th><th className="sr-num">Win%</th><th className="sr-num">Fair</th>
                <th className="sr-num" title="Composite model score">Score</th>
                <th className="sr-num" title="Factor 1 — peaking">Peak</th><th className="sr-num" title="Factor 2 — ability">Abil</th>
                <th className="sr-num">Rel.</th><th>Engine</th><th>State</th>
              </tr></thead>
              <tbody>{rc.runners.map((r) => <RunnerRow key={r.runner} r={r} />)}</tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ModelInspection;
